import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Tag } from '../tag/tag.component';

@Component({
  selector: 'app-tags',
  templateUrl: './tags.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'flex w-full min-w-0' },
})
export class TagsComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() tags: Tag[] = [];
  @Input() selected = false;
  @Input() compact = false;

  @ViewChild('container') private container?: ElementRef<HTMLElement>;
  @ViewChild('counterMeasure') private counterMeasure?: ElementRef<HTMLElement>;
  @ViewChildren('tagElement', { read: ElementRef }) private tagElements?: QueryList<ElementRef<HTMLElement>>;

  protected visibleCount = 0;
  private resizeObserver?: ResizeObserver;
  private frame?: number;
  private isViewReady = false;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  get visibleTags(): Tag[] {
    return this.tags.slice(0, this.visibleCount);
  }

  get hiddenTagsCount(): number {
    return Math.max(this.tags.length - this.visibleCount, 0);
  }

  get hiddenTagsTitle(): string {
    return this.tags
      .slice(this.visibleCount)
      .map((tag) => tag.name)
      .join(', ');
  }

  ngAfterViewInit(): void {
    this.isViewReady = true;
    this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
    this.resizeObserver.observe(this.container!.nativeElement);
    this.scheduleLayout();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tags'] || changes['compact']) {
      this.scheduleLayout();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  private scheduleLayout(): void {
    if (!this.isViewReady) return;
    if (this.frame) cancelAnimationFrame(this.frame);

    this.visibleCount = this.tags.length;
    this.changeDetectorRef.detectChanges();
    this.frame = requestAnimationFrame(() => this.updateVisibleCount());
  }

  private updateVisibleCount(): void {
    this.frame = undefined;
    const container = this.container?.nativeElement;
    const tagElements = this.tagElements?.toArray() || [];
    const counterMeasure = this.counterMeasure?.nativeElement;
    if (!container || !counterMeasure) return;

    const availableWidth = container.clientWidth;
    const gap = Number.parseFloat(getComputedStyle(container).columnGap) || 0;
    const counterWidth = counterMeasure.getBoundingClientRect().width;
    let usedWidth = 0;
    let visibleCount = 0;

    for (let index = 0; index < tagElements.length; index++) {
      const tagWidth = tagElements[index].nativeElement.getBoundingClientRect().width;
      const nextUsedWidth = usedWidth + (visibleCount ? gap : 0) + tagWidth;
      const hiddenTagsRemain = index < this.tags.length - 1;
      const requiredWidth = nextUsedWidth + (hiddenTagsRemain ? gap + counterWidth : 0);

      if (requiredWidth > availableWidth) break;
      usedWidth = nextUsedWidth;
      visibleCount++;
    }

    if (this.visibleCount !== visibleCount) {
      this.visibleCount = visibleCount;
      this.changeDetectorRef.detectChanges();
    }
  }
}
